// Canonical Pokémon TYPE table for National Dex #1..#1025.
//
// Source: standard main-series default-form typings (Gen 1-9). These are the
// well-known canonical primary/secondary types for each species' base/default
// form — NOT regional variants (Alolan/Galarian/Hisuian/Paldean), NOT Mega
// Evolutions, NOT alternate/forme-specific typings. Single-typed species use a
// one-element array (e.g. 4: ["fire"]); dual-typed use two (e.g. 6:
// ["fire","flying"]).
//
// Used by the Dex "filter by type" feature so that filtering works across the
// entire National Dex regardless of whether a species is in the curated
// POKEMON dataset.

import type { PokeType } from "../lib/types";

export const SPECIES_TYPES: Record<number, [PokeType, PokeType?]> = {
  // ── Gen 1 (Kanto) #1–151 ──────────────────────────────────────────────
  1: ["grass", "poison"], 2: ["grass", "poison"], 3: ["grass", "poison"],
  4: ["fire"], 5: ["fire"], 6: ["fire", "flying"],
  7: ["water"], 8: ["water"], 9: ["water"],
  10: ["bug"], 11: ["bug"], 12: ["bug", "flying"],
  13: ["bug", "poison"], 14: ["bug", "poison"], 15: ["bug", "poison"],
  16: ["normal", "flying"], 17: ["normal", "flying"], 18: ["normal", "flying"],
  19: ["normal"], 20: ["normal"],
  21: ["normal", "flying"], 22: ["normal", "flying"],
  23: ["poison"], 24: ["poison"],
  25: ["electric"], 26: ["electric"],
  27: ["ground"], 28: ["ground"],
  29: ["poison"], 30: ["poison"], 31: ["poison", "ground"],
  32: ["poison"], 33: ["poison"], 34: ["poison", "ground"],
  35: ["fairy"], 36: ["fairy"],
  37: ["fire"], 38: ["fire"],
  39: ["normal", "fairy"], 40: ["normal", "fairy"],
  41: ["poison", "flying"], 42: ["poison", "flying"],
  43: ["grass", "poison"], 44: ["grass", "poison"], 45: ["grass", "poison"],
  46: ["bug", "grass"], 47: ["bug", "grass"],
  48: ["bug", "poison"], 49: ["bug", "poison"],
  50: ["ground"], 51: ["ground"],
  52: ["normal"], 53: ["normal"],
  54: ["water"], 55: ["water"],
  56: ["fighting"], 57: ["fighting"],
  58: ["fire"], 59: ["fire"],
  60: ["water"], 61: ["water"], 62: ["water", "fighting"],
  63: ["psychic"], 64: ["psychic"], 65: ["psychic"],
  66: ["fighting"], 67: ["fighting"], 68: ["fighting"],
  69: ["grass", "poison"], 70: ["grass", "poison"], 71: ["grass", "poison"],
  72: ["water", "poison"], 73: ["water", "poison"],
  74: ["rock", "ground"], 75: ["rock", "ground"], 76: ["rock", "ground"],
  77: ["fire"], 78: ["fire"],
  79: ["water", "psychic"], 80: ["water", "psychic"],
  81: ["electric", "steel"], 82: ["electric", "steel"],
  83: ["normal", "flying"],
  84: ["normal", "flying"], 85: ["normal", "flying"],
  86: ["water"], 87: ["water", "ice"],
  88: ["poison"], 89: ["poison"],
  90: ["water"], 91: ["water", "ice"],
  92: ["ghost", "poison"], 93: ["ghost", "poison"], 94: ["ghost", "poison"],
  95: ["rock", "ground"],
  96: ["psychic"], 97: ["psychic"],
  98: ["water"], 99: ["water"],
  100: ["electric"], 101: ["electric"],
  102: ["grass", "psychic"], 103: ["grass", "psychic"],
  104: ["ground"], 105: ["ground"],
  106: ["fighting"], 107: ["fighting"],
  108: ["normal"],
  109: ["poison"], 110: ["poison"],
  111: ["ground", "rock"], 112: ["ground", "rock"],
  113: ["normal"],
  114: ["grass"],
  115: ["normal"],
  116: ["water"], 117: ["water"],
  118: ["water"], 119: ["water"],
  120: ["water"], 121: ["water", "psychic"],
  122: ["psychic", "fairy"],
  123: ["bug", "flying"],
  124: ["ice", "psychic"],
  125: ["electric"], 126: ["fire"],
  127: ["bug"],
  128: ["normal"],
  129: ["water"], 130: ["water", "flying"],
  131: ["water", "ice"],
  132: ["normal"],
  133: ["normal"], 134: ["water"], 135: ["electric"], 136: ["fire"],
  137: ["normal"],
  138: ["rock", "water"], 139: ["rock", "water"],
  140: ["rock", "water"], 141: ["rock", "water"],
  142: ["rock", "flying"],
  143: ["normal"],
  144: ["ice", "flying"], 145: ["electric", "flying"], 146: ["fire", "flying"],
  147: ["dragon"], 148: ["dragon"], 149: ["dragon", "flying"],
  150: ["psychic"], 151: ["psychic"],

  // ── Gen 2 (Johto) #152–251 ────────────────────────────────────────────
  152: ["grass"], 153: ["grass"], 154: ["grass"],
  155: ["fire"], 156: ["fire"], 157: ["fire"],
  158: ["water"], 159: ["water"], 160: ["water"],
  161: ["normal"], 162: ["normal"],
  163: ["normal", "flying"], 164: ["normal", "flying"],
  165: ["bug", "flying"], 166: ["bug", "flying"],
  167: ["bug", "poison"], 168: ["bug", "poison"],
  169: ["poison", "flying"],
  170: ["water", "electric"], 171: ["water", "electric"],
  172: ["electric"],
  173: ["fairy"], 174: ["normal", "fairy"], 175: ["fairy"], 176: ["fairy", "flying"],
  177: ["psychic", "flying"], 178: ["psychic", "flying"],
  179: ["electric"], 180: ["electric"], 181: ["electric"],
  182: ["grass"],
  183: ["water", "fairy"], 184: ["water", "fairy"],
  185: ["rock"],
  186: ["water"],
  187: ["grass", "flying"], 188: ["grass", "flying"], 189: ["grass", "flying"],
  190: ["normal"],
  191: ["grass"], 192: ["grass"],
  193: ["bug", "flying"],
  194: ["water", "ground"], 195: ["water", "ground"],
  196: ["psychic"], 197: ["dark"],
  198: ["dark", "flying"],
  199: ["water", "psychic"],
  200: ["ghost"],
  201: ["psychic"],
  202: ["psychic"],
  203: ["normal", "psychic"],
  204: ["bug"], 205: ["bug", "steel"],
  206: ["normal"],
  207: ["ground", "flying"],
  208: ["steel", "ground"],
  209: ["fairy"], 210: ["fairy"],
  211: ["water", "poison"],
  212: ["bug", "steel"],
  213: ["bug", "rock"],
  214: ["bug", "fighting"],
  215: ["dark", "ice"],
  216: ["normal"], 217: ["normal"],
  218: ["fire"], 219: ["fire", "rock"],
  220: ["ice", "ground"], 221: ["ice", "ground"],
  222: ["water", "rock"],
  223: ["water"], 224: ["water"],
  225: ["ice", "flying"],
  226: ["water", "flying"],
  227: ["steel", "flying"],
  228: ["dark", "fire"], 229: ["dark", "fire"],
  230: ["water", "dragon"],
  231: ["ground"], 232: ["ground"],
  233: ["normal"],
  234: ["normal"],
  235: ["normal"],
  236: ["fighting"],
  237: ["fighting"],
  238: ["ice", "psychic"],
  239: ["electric"], 240: ["fire"],
  241: ["normal"],
  242: ["normal"],
  243: ["electric"], 244: ["fire"], 245: ["water"],
  246: ["rock", "ground"], 247: ["rock", "ground"], 248: ["rock", "dark"],
  249: ["psychic", "flying"], 250: ["fire", "flying"],
  251: ["psychic", "grass"],

  // ── Gen 3 (Hoenn) #252–386 ────────────────────────────────────────────
  252: ["grass"], 253: ["grass"], 254: ["grass"],
  255: ["fire"], 256: ["fire", "fighting"], 257: ["fire", "fighting"],
  258: ["water"], 259: ["water", "ground"], 260: ["water", "ground"],
  261: ["dark"], 262: ["dark"],
  263: ["normal"], 264: ["normal"],
  265: ["bug"], 266: ["bug"], 267: ["bug", "flying"],
  268: ["bug"], 269: ["bug", "poison"],
  270: ["water", "grass"], 271: ["water", "grass"], 272: ["water", "grass"],
  273: ["grass"], 274: ["grass", "dark"], 275: ["grass", "dark"],
  276: ["normal", "flying"], 277: ["normal", "flying"],
  278: ["water", "flying"], 279: ["water", "flying"],
  280: ["psychic", "fairy"], 281: ["psychic", "fairy"], 282: ["psychic", "fairy"],
  283: ["bug", "water"], 284: ["bug", "flying"],
  285: ["grass"], 286: ["grass", "fighting"],
  287: ["normal"], 288: ["normal"], 289: ["normal"],
  290: ["bug", "ground"], 291: ["bug", "flying"], 292: ["bug", "ghost"],
  293: ["normal"], 294: ["normal"], 295: ["normal"],
  296: ["fighting"], 297: ["fighting"],
  298: ["water", "fairy"],
  299: ["rock"],
  300: ["normal"], 301: ["normal"],
  302: ["dark", "ghost"],
  303: ["steel", "fairy"],
  304: ["steel", "rock"], 305: ["steel", "rock"], 306: ["steel", "rock"],
  307: ["fighting", "psychic"], 308: ["fighting", "psychic"],
  309: ["electric"], 310: ["electric"],
  311: ["electric"], 312: ["electric"],
  313: ["bug"], 314: ["bug"],
  315: ["grass", "poison"],
  316: ["poison"], 317: ["poison"],
  318: ["water", "dark"], 319: ["water", "dark"],
  320: ["water"], 321: ["water"],
  322: ["fire", "ground"], 323: ["fire", "ground"],
  324: ["fire"],
  325: ["psychic"], 326: ["psychic"],
  327: ["normal"],
  328: ["ground"], 329: ["ground", "dragon"], 330: ["ground", "dragon"],
  331: ["grass"], 332: ["grass", "dark"],
  333: ["normal", "flying"], 334: ["dragon", "flying"],
  335: ["normal"],
  336: ["poison"],
  337: ["rock", "psychic"], 338: ["rock", "psychic"],
  339: ["water", "ground"], 340: ["water", "ground"],
  341: ["water"], 342: ["water", "dark"],
  343: ["ground", "psychic"], 344: ["ground", "psychic"],
  345: ["rock", "grass"], 346: ["rock", "grass"],
  347: ["rock", "bug"], 348: ["rock", "bug"],
  349: ["water"], 350: ["water"],
  351: ["normal"],
  352: ["normal"],
  353: ["ghost"], 354: ["ghost"],
  355: ["ghost"], 356: ["ghost"],
  357: ["grass", "flying"],
  358: ["psychic"],
  359: ["dark"],
  360: ["psychic"],
  361: ["ice"], 362: ["ice"],
  363: ["ice", "water"], 364: ["ice", "water"], 365: ["ice", "water"],
  366: ["water"], 367: ["water"], 368: ["water"],
  369: ["water", "rock"],
  370: ["water"],
  371: ["dragon"], 372: ["dragon"], 373: ["dragon", "flying"],
  374: ["steel", "psychic"], 375: ["steel", "psychic"], 376: ["steel", "psychic"],
  377: ["rock"], 378: ["ice"], 379: ["steel"],
  380: ["dragon", "psychic"], 381: ["dragon", "psychic"],
  382: ["water"], 383: ["ground"], 384: ["dragon", "flying"],
  385: ["steel", "psychic"], 386: ["psychic"],

  // ── Gen 4 (Sinnoh) #387–493 ───────────────────────────────────────────
  387: ["grass"], 388: ["grass"], 389: ["grass", "ground"],
  390: ["fire"], 391: ["fire", "fighting"], 392: ["fire", "fighting"],
  393: ["water"], 394: ["water"], 395: ["water", "steel"],
  396: ["normal", "flying"], 397: ["normal", "flying"], 398: ["normal", "flying"],
  399: ["normal"], 400: ["normal"],
  401: ["bug"], 402: ["bug"],
  403: ["electric"], 404: ["electric"], 405: ["electric"],
  406: ["grass", "poison"],
  407: ["grass", "poison"],
  408: ["rock"], 409: ["rock"],
  410: ["rock", "steel"], 411: ["rock", "steel"],
  412: ["bug"], 413: ["bug", "grass"], 414: ["bug", "flying"],
  415: ["bug", "flying"], 416: ["bug", "flying"],
  417: ["electric"],
  418: ["water"], 419: ["water"],
  420: ["grass"], 421: ["grass"],
  422: ["water"], 423: ["water", "ground"],
  424: ["normal"],
  425: ["ghost", "flying"], 426: ["ghost", "flying"],
  427: ["normal"], 428: ["normal"],
  429: ["ghost"],
  430: ["dark", "flying"],
  431: ["normal"], 432: ["normal"],
  433: ["psychic"],
  434: ["poison", "dark"], 435: ["poison", "dark"],
  436: ["steel", "psychic"], 437: ["steel", "psychic"],
  438: ["rock"],
  439: ["psychic", "fairy"],
  440: ["normal"],
  441: ["normal", "flying"],
  442: ["ghost", "dark"],
  443: ["dragon", "ground"], 444: ["dragon", "ground"], 445: ["dragon", "ground"],
  446: ["normal"],
  447: ["fighting"], 448: ["fighting", "steel"],
  449: ["ground"], 450: ["ground"],
  451: ["poison", "bug"], 452: ["poison", "dark"],
  453: ["poison", "fighting"], 454: ["poison", "fighting"],
  455: ["grass"],
  456: ["water"], 457: ["water"],
  458: ["water", "flying"], 459: ["grass", "ice"], 460: ["grass", "ice"],
  461: ["dark", "ice"],
  462: ["electric", "steel"],
  463: ["normal"],
  464: ["ground", "rock"],
  465: ["grass"],
  466: ["electric"], 467: ["fire"],
  468: ["fairy", "flying"],
  469: ["bug", "flying"],
  470: ["grass"], 471: ["ice"],
  472: ["ground", "flying"],
  473: ["ice", "ground"],
  474: ["normal"],
  475: ["psychic", "fighting"],
  476: ["rock", "steel"],
  477: ["ghost"],
  478: ["ice", "ghost"],
  479: ["electric", "ghost"],
  480: ["psychic"], 481: ["psychic"], 482: ["psychic"],
  483: ["steel", "dragon"], 484: ["water", "dragon"],
  485: ["fire", "steel"],
  486: ["normal"],
  487: ["ghost", "dragon"],
  488: ["psychic"],
  489: ["water"], 490: ["water"],
  491: ["dark"],
  492: ["grass"],
  493: ["normal"],

  // ── Gen 5 (Unova) #494–649 ────────────────────────────────────────────
  494: ["psychic", "fire"],
  495: ["grass"], 496: ["grass"], 497: ["grass"],
  498: ["fire"], 499: ["fire", "fighting"], 500: ["fire", "fighting"],
  501: ["water"], 502: ["water"], 503: ["water"],
  504: ["normal"], 505: ["normal"],
  506: ["normal"], 507: ["normal"], 508: ["normal"],
  509: ["dark"], 510: ["dark"],
  511: ["grass"], 512: ["grass"],
  513: ["fire"], 514: ["fire"],
  515: ["water"], 516: ["water"],
  517: ["psychic"], 518: ["psychic"],
  519: ["normal", "flying"], 520: ["normal", "flying"], 521: ["normal", "flying"],
  522: ["electric"], 523: ["electric"],
  524: ["rock"], 525: ["rock"], 526: ["rock"],
  527: ["psychic", "flying"], 528: ["psychic", "flying"],
  529: ["ground"], 530: ["ground", "steel"],
  531: ["normal"],
  532: ["fighting"], 533: ["fighting"], 534: ["fighting"],
  535: ["water"], 536: ["water", "ground"], 537: ["water", "ground"],
  538: ["fighting"], 539: ["fighting"],
  540: ["bug", "grass"], 541: ["bug", "grass"], 542: ["bug", "grass"],
  543: ["bug", "poison"], 544: ["bug", "poison"], 545: ["bug", "poison"],
  546: ["grass", "fairy"], 547: ["grass", "fairy"],
  548: ["grass"], 549: ["grass"],
  550: ["water"],
  551: ["ground", "dark"], 552: ["ground", "dark"], 553: ["ground", "dark"],
  554: ["fire"], 555: ["fire"],
  556: ["grass"],
  557: ["bug", "rock"], 558: ["bug", "rock"],
  559: ["dark", "fighting"], 560: ["dark", "fighting"],
  561: ["psychic", "flying"],
  562: ["ghost"], 563: ["ghost"],
  564: ["water", "rock"], 565: ["water", "rock"],
  566: ["rock", "flying"], 567: ["rock", "flying"],
  568: ["poison"], 569: ["poison"],
  570: ["dark"], 571: ["dark"],
  572: ["normal"], 573: ["normal"],
  574: ["psychic"], 575: ["psychic"], 576: ["psychic"],
  577: ["psychic"], 578: ["psychic"], 579: ["psychic"],
  580: ["water", "flying"], 581: ["water", "flying"],
  582: ["ice"], 583: ["ice"], 584: ["ice"],
  585: ["normal", "grass"], 586: ["normal", "grass"],
  587: ["electric", "flying"],
  588: ["bug"], 589: ["bug", "steel"],
  590: ["grass", "poison"], 591: ["grass", "poison"],
  592: ["water", "ghost"], 593: ["water", "ghost"],
  594: ["water"],
  595: ["bug", "electric"], 596: ["bug", "electric"],
  597: ["grass", "steel"], 598: ["grass", "steel"],
  599: ["steel"], 600: ["steel"], 601: ["steel"],
  602: ["electric"], 603: ["electric"], 604: ["electric"],
  605: ["psychic"], 606: ["psychic"],
  607: ["ghost", "fire"], 608: ["ghost", "fire"], 609: ["ghost", "fire"],
  610: ["dragon"], 611: ["dragon"], 612: ["dragon"],
  613: ["ice"], 614: ["ice"],
  615: ["ice"],
  616: ["bug"], 617: ["bug"],
  618: ["ground", "electric"],
  619: ["fighting"], 620: ["fighting"],
  621: ["dragon"],
  622: ["ground", "ghost"], 623: ["ground", "ghost"],
  624: ["dark", "steel"], 625: ["dark", "steel"],
  626: ["normal"],
  627: ["normal", "flying"], 628: ["normal", "flying"],
  629: ["dark", "flying"], 630: ["dark", "flying"],
  631: ["fire"],
  632: ["bug", "steel"],
  633: ["dark"], 634: ["dark"], 635: ["dark", "dragon"],
  636: ["bug", "fire"], 637: ["bug", "fire"],
  638: ["steel", "fighting"], 639: ["rock", "fighting"], 640: ["grass", "fighting"],
  641: ["flying"], 642: ["electric", "flying"],
  643: ["dragon", "fire"], 644: ["dragon", "electric"],
  645: ["ground", "flying"], 646: ["dragon", "ice"],
  647: ["water", "fighting"], 648: ["normal", "psychic"],
  649: ["bug", "steel"],

  // ── Gen 6 (Kalos) #650–721 ────────────────────────────────────────────
  650: ["grass"], 651: ["grass"], 652: ["grass", "fighting"],
  653: ["fire"], 654: ["fire"], 655: ["fire", "psychic"],
  656: ["water"], 657: ["water"], 658: ["water", "dark"],
  659: ["normal"], 660: ["normal", "ground"],
  661: ["normal", "flying"], 662: ["fire", "flying"], 663: ["fire", "flying"],
  664: ["bug"], 665: ["bug"], 666: ["bug", "flying"],
  667: ["fire", "normal"], 668: ["fire", "normal"],
  669: ["fairy"], 670: ["fairy"], 671: ["fairy"],
  672: ["grass"], 673: ["grass"],
  674: ["fighting"], 675: ["fighting", "dark"],
  676: ["normal"],
  677: ["psychic"], 678: ["psychic"],
  679: ["steel", "ghost"], 680: ["steel", "ghost"], 681: ["steel", "ghost"],
  682: ["fairy"], 683: ["fairy"],
  684: ["fairy"], 685: ["fairy"],
  686: ["dark", "psychic"], 687: ["dark", "psychic"],
  688: ["rock", "water"], 689: ["rock", "water"],
  690: ["poison", "water"], 691: ["poison", "dragon"],
  692: ["water"], 693: ["water"],
  694: ["electric", "normal"], 695: ["electric", "normal"],
  696: ["rock", "dragon"], 697: ["rock", "dragon"],
  698: ["rock", "ice"], 699: ["rock", "ice"],
  700: ["fairy"],
  701: ["fighting", "flying"],
  702: ["electric", "fairy"],
  703: ["rock", "fairy"],
  704: ["dragon"], 705: ["dragon"], 706: ["dragon"],
  707: ["steel", "fairy"],
  708: ["ghost", "grass"], 709: ["ghost", "grass"],
  710: ["ghost", "grass"], 711: ["ghost", "grass"],
  712: ["ice"], 713: ["ice"],
  714: ["flying", "dragon"], 715: ["flying", "dragon"],
  716: ["fairy"], 717: ["dark", "flying"], 718: ["dragon", "ground"],
  719: ["rock", "fairy"], 720: ["psychic", "ghost"],
  721: ["fire", "water"],

  // ── Gen 7 (Alola) #722–809 ────────────────────────────────────────────
  722: ["grass", "flying"], 723: ["grass", "flying"], 724: ["grass", "ghost"],
  725: ["fire"], 726: ["fire"], 727: ["fire", "dark"],
  728: ["water"], 729: ["water"], 730: ["water", "fairy"],
  731: ["normal", "flying"], 732: ["normal", "flying"], 733: ["normal", "flying"],
  734: ["normal"], 735: ["normal"],
  736: ["bug"], 737: ["bug", "electric"], 738: ["bug", "electric"],
  739: ["fighting"], 740: ["fighting", "ice"],
  741: ["fire", "flying"],
  742: ["bug", "fairy"], 743: ["bug", "fairy"],
  744: ["rock"], 745: ["rock"],
  746: ["water"],
  747: ["poison", "water"], 748: ["poison", "water"],
  749: ["ground"], 750: ["ground"],
  751: ["water", "bug"], 752: ["water", "bug"],
  753: ["grass"], 754: ["grass"],
  755: ["grass", "fairy"], 756: ["grass", "fairy"],
  757: ["poison", "fire"], 758: ["poison", "fire"],
  759: ["normal", "fighting"], 760: ["normal", "fighting"],
  761: ["grass"], 762: ["grass"], 763: ["grass"],
  764: ["fairy"],
  765: ["normal", "psychic"],
  766: ["fighting"],
  767: ["bug", "water"], 768: ["bug", "water"],
  769: ["ghost", "ground"], 770: ["ghost", "ground"],
  771: ["water"],
  772: ["normal"], 773: ["normal"],
  774: ["rock", "flying"],
  775: ["normal"],
  776: ["fire", "dragon"],
  777: ["electric", "steel"],
  778: ["ghost", "fairy"],
  779: ["water", "psychic"],
  780: ["normal", "dragon"],
  781: ["ghost", "grass"],
  782: ["dragon"], 783: ["dragon", "fighting"], 784: ["dragon", "fighting"],
  785: ["electric", "fairy"], 786: ["psychic", "fairy"],
  787: ["grass", "fairy"], 788: ["water", "fairy"],
  789: ["psychic"], 790: ["psychic"], 791: ["psychic", "steel"],
  792: ["psychic", "ghost"],
  793: ["rock", "poison"], 794: ["bug", "fighting"], 795: ["bug", "fighting"],
  796: ["electric"], 797: ["steel", "flying"], 798: ["grass", "steel"],
  799: ["dark", "dragon"], 800: ["psychic"],
  801: ["steel", "fairy"], 802: ["fighting", "ghost"],
  803: ["poison"], 804: ["poison", "dragon"],
  805: ["rock", "steel"], 806: ["fire", "ghost"],
  807: ["electric"],
  808: ["steel"], 809: ["steel"],

  // ── Gen 8 (Galar + Hisui evolutions) #810–905 ─────────────────────────
  810: ["grass"], 811: ["grass"], 812: ["grass"],
  813: ["fire"], 814: ["fire"], 815: ["fire"],
  816: ["water"], 817: ["water"], 818: ["water"],
  819: ["normal"], 820: ["normal"],
  821: ["flying"], 822: ["flying"], 823: ["flying", "steel"],
  824: ["bug"], 825: ["bug", "psychic"], 826: ["bug", "psychic"],
  827: ["dark"], 828: ["dark"],
  829: ["grass"], 830: ["grass"],
  831: ["normal"], 832: ["normal"],
  833: ["water"], 834: ["water"],
  835: ["electric"], 836: ["electric"],
  837: ["rock"], 838: ["rock", "fire"], 839: ["rock", "fire"],
  840: ["grass", "dragon"], 841: ["grass", "dragon"], 842: ["grass", "dragon"],
  843: ["ground"], 844: ["ground"],
  845: ["water", "flying"],
  846: ["water"], 847: ["water"],
  848: ["electric", "poison"], 849: ["electric", "poison"],
  850: ["fire", "bug"], 851: ["fire", "bug"],
  852: ["fighting"], 853: ["fighting"],
  854: ["ghost"], 855: ["ghost"],
  856: ["psychic"], 857: ["psychic"], 858: ["psychic"],
  859: ["dark", "fairy"], 860: ["dark", "fairy"], 861: ["dark", "fairy"],
  862: ["dark", "normal"],
  863: ["steel"],
  864: ["ghost"],
  865: ["fighting", "flying"],
  866: ["ice", "psychic"],
  867: ["ground", "ghost"],
  868: ["fairy"], 869: ["fairy"],
  870: ["fighting"],
  871: ["electric"],
  872: ["ice"], 873: ["ice"],
  874: ["rock"],
  875: ["ice"],
  876: ["psychic", "normal"],
  877: ["electric"],
  878: ["steel"], 879: ["steel"],
  880: ["electric", "dragon"],
  881: ["electric", "ice"],
  882: ["water", "dragon"],
  883: ["water", "dragon"],
  884: ["steel", "dragon"],
  885: ["dragon", "ghost"], 886: ["dragon", "ghost"], 887: ["dragon", "ghost"],
  888: ["fairy"], 889: ["fighting"],
  890: ["poison", "dragon"],
  891: ["fighting"], 892: ["fighting"],
  893: ["dark", "grass"],
  894: ["electric"], 895: ["dragon"],
  896: ["ice"], 897: ["ghost"], 898: ["psychic", "grass"],
  899: ["normal", "psychic"], 900: ["bug", "rock"], 901: ["ground", "normal"],
  902: ["water", "ghost"], 903: ["fighting", "poison"], 904: ["dark", "poison"],
  905: ["fairy", "flying"],

  // ── Gen 9 (Paldea) #906–1025 ──────────────────────────────────────────
  906: ["grass"], 907: ["grass"], 908: ["grass", "dark"],
  909: ["fire"], 910: ["fire"], 911: ["fire", "ghost"],
  912: ["water"], 913: ["water"], 914: ["water"],
  915: ["normal"], 916: ["normal"],
  917: ["bug"], 918: ["bug"],
  919: ["bug", "grass"], 920: ["bug", "grass"],
  921: ["electric"], 922: ["electric"], 923: ["electric"],
  924: ["normal"], 925: ["normal"],
  926: ["fairy"], 927: ["fairy"],
  928: ["grass"], 929: ["grass"], 930: ["grass"],
  931: ["normal", "flying"],
  932: ["rock"], 933: ["rock"], 934: ["rock"],
  935: ["fire"], 936: ["fire"], 937: ["fire", "ghost"],
  938: ["electric"], 939: ["electric"],
  940: ["electric", "flying"], 941: ["electric", "flying"],
  942: ["dark"], 943: ["dark"],
  944: ["poison"], 945: ["poison", "normal"],
  946: ["grass"], 947: ["grass"],
  948: ["ground", "grass"], 949: ["ground", "grass"],
  950: ["rock"],
  951: ["grass"], 952: ["grass", "fighting"],
  953: ["bug"], 954: ["bug", "psychic"],
  955: ["psychic"], 956: ["psychic", "flying"],
  957: ["fairy", "steel"], 958: ["fairy", "steel"], 959: ["fairy", "steel"],
  960: ["water"], 961: ["water"],
  962: ["flying"],
  963: ["water"], 964: ["water"],
  965: ["steel"], 966: ["steel"],
  967: ["electric"],
  968: ["bug", "dragon"],
  969: ["rock"], 970: ["rock", "electric"],
  971: ["dark"], 972: ["dark"],
  973: ["fire"],
  974: ["ice"], 975: ["ice", "water"],
  976: ["water"],
  977: ["water"],
  978: ["water", "dragon"],
  979: ["fighting", "ghost"],
  980: ["poison", "ground"],
  981: ["normal"],
  982: ["normal"],
  983: ["dark", "steel"],
  984: ["bug", "rock"],
  985: ["fairy", "steel"],
  986: ["dark", "fighting"],
  987: ["ghost", "fairy"],
  988: ["bug"],
  989: ["electric"],
  990: ["steel", "poison"],
  991: ["fire", "psychic"],
  992: ["rock", "psychic"],
  993: ["bug", "fighting"],
  994: ["steel", "fire"],
  995: ["steel", "dragon"],
  996: ["dragon", "ice"], 997: ["dragon", "ice"], 998: ["dragon", "ice"],
  999: ["steel", "ghost"], 1000: ["steel", "ghost"],
  1001: ["fighting", "ghost"], 1002: ["fighting", "ghost"],
  1003: ["fire", "ghost"], 1004: ["fire", "ghost"],
  1005: ["fighting", "dragon"], 1006: ["electric", "dragon"],
  1007: ["fighting", "dragon"], 1008: ["electric", "dragon"],
  1009: ["water", "dragon"], 1010: ["fire", "dragon"],
  1011: ["grass"],
  1012: ["grass", "fighting"], 1013: ["grass", "fighting"],
  1014: ["grass", "poison"], 1015: ["grass", "poison"],
  1016: ["poison", "normal"],
  1017: ["grass", "ghost"],
  1018: ["ground", "poison"],
  1019: ["grass", "poison"],
  1020: ["fire", "dragon"], 1021: ["electric", "dragon"],
  1022: ["ghost", "dragon"], 1023: ["psychic", "dragon"],
  1024: ["normal"],
  1025: ["poison", "ghost"],
};

// Optional, cheap, side-effect-free self-check. Guarded so it never runs on a
// normal import — only when explicitly opted in via the environment variable
// SPECIES_TYPES_SELFCHECK. Asserts every id 1..1025 is present and that every
// type string belongs to the canonical PokeType union.
if (typeof process !== "undefined" && process.env.SPECIES_TYPES_SELFCHECK) {
  const UNION: ReadonlySet<string> = new Set([
    "normal", "fire", "water", "electric", "grass", "ice",
    "fighting", "poison", "ground", "flying", "psychic", "bug",
    "rock", "ghost", "dragon", "dark", "steel", "fairy",
  ]);
  const missing: number[] = [];
  const bad: string[] = [];
  for (let id = 1; id <= 1025; id++) {
    const entry = SPECIES_TYPES[id];
    if (!entry) { missing.push(id); continue; }
    for (const t of entry) {
      if (t !== undefined && !UNION.has(t)) bad.push(`#${id}:${t}`);
    }
  }
  if (missing.length) throw new Error(`SPECIES_TYPES missing ids: ${missing.join(", ")}`);
  if (bad.length) throw new Error(`SPECIES_TYPES invalid types: ${bad.join(", ")}`);
}
